import { TaskStatusDto, TaskTriggerDto, UserRoleDto } from '@/api/generated';

export type LandingPageTranslation = {
  dashboard: {
    activeSession: string;
    activity: { continue: string; learnMore: string };
    aiAssistant: { label: string };
    employee: {
      activitiesTitle: string;
      awaitingSupervisorDescription: string;
      awaitingSupervisorTitle: string;
      createPlan: string;
      dcFeedbackDescription: string;
      dcFeedbackTitle: string;
      moreActivities: string;
      moreTasks: string;
      myDevelopment: string;
      myProfile: string;
      noActivities: string;
      noPlanDesc: string;
      noPlanTitle: string;
      noStepsDescription: string;
      noStepsTitle: string;
      noTasks: string;
      pausedDescription: string;
      pausedTitle: string;
      planTitle: string;
      subtitle: string;
      tasksTitle: string;
      title: string;
    };
    greeting: string;
    hrbp: {
      areaHint: string;
      changes: {
        absenceReturns: string;
        details: {
          absenceReturnsScope: string;
          absenceReturnsTitle: string;
          dateTo: string;
          employee: string;
          empty: string;
          endingContractsScope: string;
          endingContractsTitle: string;
          headcountTitle: string;
          moreContracts: string;
          moreReturns: string;
          returnDate: string;
        };
        empty: string;
        endingContracts: string;
        headcount: string;
        title: string;
      };
      goToArea: string;
      goToSuccession: string;
      kpi: {
        agedFiftyFivePlus: string;
        employees: string;
        highRiskOfLeaving: string;
        inDevelopmentPrograms: string;
        leadershipPotential: string;
        ready1To2Years: string;
        ready3To5Years: string;
        readyNow: string;
        readyUpTo1Year: string;
      };
      reactions: {
        empty: string;
        highRiskOfLeaving: string;
        title: string;
        withoutPlan: string;
      };
      subtitle: string;
      title: string;
    };
    manager: {
      approve: string;
      fullOverview: string;
      kpi: { noPlan: string; overdueSteps: string; plansToApprove: string; teamSize: string };
      myTeam: string;
      needsReaction: string;
      noActivityDays: string;
      noAttention: string;
      noDevelopmentPlan: string;
      onTrack: string;
      overdueDaysAgo: string;
      overdueStep: string;
      planToApprove: string;
      subtitle: string;
      teamState: string;
      title: string;
      waitingDays: string;
    };
    news: {
      add: string;
      all: string;
      backToList: string;
      backToStart: string;
      close: string;
      development: string;
      empty: string;
      emptyContent: string;
      error: string;
      hideOlder: string;
      loading: string;
      openInHub: string;
      readMore: string;
      share: string;
      shareError: string;
      shareLinkCopied: string;
      showing: string;
      showOlder: string;
      title: string;
      tutorials: string;
      updates: string;
      videoError: string;
      yesterday: string;
    };
    noRoles: string;
    notifications: string;
    platformStats: {
      entriesUnit: string;
      subtitle: string;
      title: string;
    };
    roles: { admin: string; employee: string; hrbp: string; manager: string };
    selectRoleHint: string;
    subtitle: string;
    task: {
      status: Record<'overdue' | TaskStatusDto, string>;
      type: Record<TaskTriggerDto, string>;
    };
    yourRoles: string;
  };
  finalStatement: {
    subtitle: string;
    title: string;
  };
  hero: {
    buttonText: Record<'Employee' | UserRoleDto, string>;
    learnMore: string;
    rotatingTexts: Record<'Employee' | UserRoleDto, string[]>;
    subtitle: string;
    title: string;
    welcomeText: string;
  };
  ourNeeds: {
    hrDatabase: {
      description: Record<'Employee' | UserRoleDto, string>;
      title: string;
    };
    newRole: {
      description: Record<'Employee' | UserRoleDto, string>;
      title: string;
    };
    successionModule: {
      description: Record<'Employee' | UserRoleDto, string>;
      title: string;
    };
    title: string;
  };
  stats: {
    andCounting: string;
    bePartOfSuccess: string;
    communityDescription: string;
    createdByUsers: string;
    joinOurCommunity: string;
    title: string;
    totalDevelopmentPlans: string;
    totalVisits: string;
    trustMessage: string;
  };
  whyJain: {
    description: {
      accented: string;
      firstPart: string;
      secondPart: string;
    };
    title: string;
  };
};

export const landingPageTranslation: LandingPageTranslation = {
  dashboard: {
    activeSession: 'Active session',
    activity: {
      continue: 'Continue',
      learnMore: 'Learn more',
    },
    aiAssistant: {
      label: 'AI Development Assistant',
    },
    employee: {
      activitiesTitle: 'MY DEVELOPMENT ACTIVITIES',
      awaitingSupervisorDescription:
        "Your manager is setting up your development plan. We'll let you know when you can add steps.",
      awaitingSupervisorTitle: 'Your manager is preparing your plan',
      createPlan: 'Define development direction',
      dcFeedbackDescription:
        'You can create a plan once your feedback meeting after the Development Center session is noted down.',
      dcFeedbackTitle: 'Waiting for the feedback meeting after the DC',
      moreActivities: '+ {{count}} more activities',
      moreTasks: '+ {{count}} more tasks',
      myDevelopment: 'My development',
      myProfile: 'My profile',
      noActivities: 'No development activities',
      noPlanDesc: 'Your development plan has not been activated yet. Contact your manager.',
      noPlanTitle: 'No active development plan',
      noStepsDescription: 'Add the first step to start tracking progress on this plan.',
      noStepsTitle: 'Start working on the plan',
      noTasks: 'No tasks',
      pausedDescription:
        'You are not covered by a development cycle right now, so you cannot create a plan. Check back once a cycle covers you.',
      pausedTitle: 'Outside a development cycle',
      planTitle: 'MY DEVELOPMENT PLAN',
      subtitle: 'as an employee',
      tasksTitle: 'MY UPCOMING TASKS',
      title: 'My tasks and development',
    },
    greeting: 'Hi',
    hrbp: {
      areaHint: 'Your area covers {{count}} employees',
      changes: {
        absenceReturns: 'Returns from absence (3+ weeks)',
        details: {
          absenceReturnsScope: '{{count}} people / 14 days',
          absenceReturnsTitle: 'Returns from absence',
          dateTo: 'End date',
          employee: 'Employee',
          empty: 'No data in this window.',
          endingContractsScope: '{{count}} people / 30 days',
          endingContractsTitle: 'Contracts ending',
          headcountTitle: 'Headcount - last 2 weeks',
          moreContracts: '+ {{count}} more contracts...',
          moreReturns: '+ {{count}} more returns...',
          returnDate: 'Return',
        },
        empty: 'No HR changes in your area.',
        endingContracts: 'Contracts ending (30 days)',
        headcount: 'Current headcount',
        title: 'HR changes',
      },
      goToArea: 'HR area',
      goToSuccession: 'Succession',
      kpi: {
        agedFiftyFivePlus: 'Age 55+',
        employees: 'Employees in area',
        highRiskOfLeaving: 'High risk of leaving',
        inDevelopmentPrograms: 'In development programs',
        leadershipPotential: 'Leadership potential',
        ready1To2Years: 'Ready in 1-2 years',
        ready3To5Years: 'Ready in 3-5 years',
        readyNow: 'Ready now',
        readyUpTo1Year: 'Ready within 1 year',
      },
      reactions: {
        empty: 'Nothing requires your reaction right now.',
        highRiskOfLeaving: 'High risk of leaving: {{count}} people',
        title: 'Needs reaction',
        withoutPlan: 'No active plan: {{count}} people',
      },
      subtitle: 'as HRBP',
      title: 'Succession, development and HR in your area',
    },
    manager: {
      approve: 'Approve',
      fullOverview: 'Full overview · {{count}} people',
      kpi: {
        noPlan: 'No development plan',
        overdueSteps: 'Overdue steps',
        plansToApprove: 'Plans to approve',
        teamSize: 'People in team',
      },
      myTeam: 'My team',
      needsReaction: 'Needs your reaction',
      noActivityDays: 'No activity for {{count}} days',
      noAttention: 'Nothing needs your attention right now.',
      noDevelopmentPlan: 'No development plan',
      onTrack: '{{count}} people are following their plan on schedule',
      overdueDaysAgo: '{{count}} days overdue',
      overdueStep: 'Overdue step',
      planToApprove: '{{name}}: plan to approve',
      subtitle: 'as supervisor',
      teamState: 'Team state',
      title: 'My team development',
      waitingDays: 'Waiting {{count}} days',
    },
    news: {
      add: 'Manage',
      all: 'All',
      backToList: 'Back to list',
      backToStart: 'Back to Start',
      close: 'Close',
      development: 'Development',
      empty: 'No news in this category yet.',
      emptyContent: 'No article content has been added yet.',
      error: 'Could not load news.',
      hideOlder: 'Hide older articles',
      loading: 'Loading article...',
      openInHub: 'Open in news hub',
      readMore: 'Read more',
      share: 'Share',
      shareError: 'Could not share the article',
      shareLinkCopied: 'Article link copied',
      showing: '{{visible}} of {{total}}',
      showOlder: 'Show older articles',
      title: 'News',
      tutorials: 'Tutorials',
      updates: 'Updates',
      videoError: 'This video cannot be played. The file may be damaged or encoded incorrectly.',
      yesterday: 'Yesterday',
    },
    noRoles: 'No roles assigned. Contact your administrator.',
    notifications: 'notifications',
    platformStats: {
      entriesUnit: 'entries',
      subtitle: 'In total since jAIn went live',
      title: 'Platform entries',
    },
    roles: {
      admin: 'Administrator',
      employee: 'Employee',
      hrbp: 'HRBP',
      manager: 'Manager',
    },
    selectRoleHint: 'Select at least one role to see a view.',
    subtitle: 'Your jAIn 3.0 workspace',
    task: {
      status: {
        overdue: 'Overdue',
        [TaskStatusDto.Cancelled]: 'Cancelled',
        [TaskStatusDto.Done]: 'Completed',
        [TaskStatusDto.InProgress]: 'In progress',
        [TaskStatusDto.Open]: 'New',
      },
      type: {
        [TaskTriggerDto.DevelopmentPlanInitiationRequired]: 'Plan initiation',
        [TaskTriggerDto.DevelopmentPlanRequired]: 'Plan required',
        [TaskTriggerDto.DevelopmentPlanReviewRequired]: 'Plan to review',
        [TaskTriggerDto.DevelopmentStepInProgress]: 'Step in progress',
        [TaskTriggerDto.DevelopmentStepOverdue]: 'Step overdue',
        [TaskTriggerDto.Manual]: 'Task',
      },
    },
    yourRoles: 'YOUR ROLES',
  },
  finalStatement: {
    subtitle: 'It is an intelligent partner',
    title: 'jAIn is more than just another system',
  },
  hero: {
    buttonText: {
      Employee: 'Go to your profile',
      [UserRoleDto.Admin]: 'Go to employee search',
      [UserRoleDto.AdminCertification]: 'Go to employee search',
      [UserRoleDto.AdminDevelopment]: 'Go to employee search',
      [UserRoleDto.AdminSuccession]: 'Go to employee search',
      [UserRoleDto.Employee]: 'Go to employee search',
      [UserRoleDto.Hrbp]: 'Go to employee search',
      [UserRoleDto.HrbpHq]: 'Go to employee search',
      [UserRoleDto.ManagerP1]: 'Go to your team',
      [UserRoleDto.ManagerP2]: 'Go to your team',
    },
    learnMore: 'Learn more',
    rotatingTexts: {
      Employee: ['personalized development plans', 'AI assistant development chat'],
      [UserRoleDto.Admin]: [
        'convenient employee search',
        'interactive succession management module',
        'employee flashcards',
        'summary of information after MDR',
        'status of development plans',
        'development chat about the employee',
      ],
      [UserRoleDto.AdminCertification]: ['convenient employee search', 'employee flashcards'],
      [UserRoleDto.AdminDevelopment]: [
        'convenient employee search',
        'employee flashcards',
        'summary of information after MDR',
        'status of development plans',
        'development chat about the employee',
      ],
      [UserRoleDto.AdminSuccession]: [
        'convenient employee search',
        'interactive succession management module',
        'employee flashcards',
      ],
      [UserRoleDto.Employee]: ['personalized development plans', 'AI assistant development chat'],
      [UserRoleDto.Hrbp]: [
        'convenient employee search',
        'interactive succession management module',
        'employee flashcards',
        'summary of information after MDR',
        'status of development plans',
        'development chat about the employee',
      ],
      [UserRoleDto.HrbpHq]: [
        'convenient employee search',
        'interactive succession management module',
        'employee flashcards',
        'summary of information after MDR',
        'status of development plans',
        'development chat about the employee',
      ],
      [UserRoleDto.ManagerP1]: [
        'employee flashcards',
        'summary of information after MDR',
        'status of development plans',
      ],
      [UserRoleDto.ManagerP2]: [
        'employee flashcards',
        'summary of information after MDR',
        'status of development plans',
      ],
    },
    subtitle: 'Discover jAIn and explore',
    title: 'Experience new development opportunities',
    welcomeText: 'Hi, ',
  },
  ourNeeds: {
    hrDatabase: {
      description: {
        Employee: '',
        [UserRoleDto.Admin]:
          'Manage all data from one place. jAIn organizes, analyses and presents data in a way that supports your HR decisions.',
        [UserRoleDto.AdminCertification]:
          'Manage all data from one place. jAIn organizes, analyses and presents data in a way that supports your HR decisions.',
        [UserRoleDto.AdminDevelopment]:
          'Manage all data from one place. jAIn organizes, analyses and presents data in a way that supports your HR decisions.',
        [UserRoleDto.AdminSuccession]:
          'Manage all data from one place. jAIn organizes, analyses and presents data in a way that supports your HR decisions.',
        [UserRoleDto.Employee]: '',
        [UserRoleDto.Hrbp]:
          'Manage all data from one place. jAIn organizes, analyses and presents data in a way that supports your HR decisions.',
        [UserRoleDto.HrbpHq]:
          'Manage all data from one place. jAIn organizes, analyses and presents data in a way that supports your HR decisions.',
        [UserRoleDto.ManagerP1]:
          'Gain quick access to your team’s data. jAIn supports you - so you can focus on what matters.',
        [UserRoleDto.ManagerP2]:
          'Gain quick access to your team’s data. jAIn supports you - so you can focus on what matters.',
      },
      title: 'Enormous HR database',
    },
    newRole: {
      description: {
        Employee:
          'You build and follow your personalized development plan. jAIn guides you on how to achieve it and take the next step.',
        [UserRoleDto.Admin]:
          "Monitor employees' readiness for new roles and responsibilities. Support the development of competencies in the whole organization - systematically.",
        [UserRoleDto.AdminCertification]:
          "Monitor employees' readiness for new roles and responsibilities. Support the development of competencies in the whole organization - systematically.",
        [UserRoleDto.AdminDevelopment]:
          "Monitor employees' readiness for new roles and responsibilities. Support the development of competencies in the whole organization - systematically.",
        [UserRoleDto.AdminSuccession]:
          "Monitor employees' readiness for new roles and responsibilities. Support the development of competencies in the whole organization - systematically.",
        [UserRoleDto.Employee]:
          'You build and follow your personalized development plan. jAIn guides you on how to achieve it and take the next step.',
        [UserRoleDto.Hrbp]:
          "Monitor employees' readiness for new roles and responsibilities. Support the development of competencies in the whole organization - systematically.",
        [UserRoleDto.HrbpHq]:
          "Monitor employees' readiness for new roles and responsibilities. Support the development of competencies in the whole organization - systematically.",
        [UserRoleDto.ManagerP1]:
          "Set the direction for development plans and support your team's growth. jAIn will suggest, who is ready for new challenges.",
        [UserRoleDto.ManagerP2]:
          "Set the direction for development plans and support your team's growth. jAIn will suggest, who is ready for new challenges.",
      },
      title: 'Development and readiness for new roles',
    },
    successionModule: {
      description: {
        Employee: '',
        [UserRoleDto.Admin]:
          'Manage the structure. Anticipate changes. Ensure continuity. jAIn allows you to create and adjust organizational structures in real time - intuitively and transparently.',
        [UserRoleDto.AdminCertification]: '',
        [UserRoleDto.AdminDevelopment]: '',
        [UserRoleDto.AdminSuccession]:
          'Manage the structure. Anticipate changes. Ensure continuity. jAIn allows you to create and adjust organizational structures in real time - intuitively and transparently.',
        [UserRoleDto.Employee]: '',
        [UserRoleDto.Hrbp]:
          'Manage the structure. Anticipate changes. Ensure continuity. jAIn allows you to create and adjust organizational structures in real time - intuitively and transparently.',
        [UserRoleDto.HrbpHq]:
          'Manage the structure. Anticipate changes. Ensure continuity. jAIn allows you to create and adjust organizational structures in real time - intuitively and transparently.',
        [UserRoleDto.ManagerP1]: '',
        [UserRoleDto.ManagerP2]: '',
      },
      title: 'Managing succession',
    },
    title: 'Our needs',
  },
  stats: {
    andCounting: 'and counting...',
    bePartOfSuccess: 'Be part of this success story!',
    communityDescription: 'We are proud of the number of users who have trusted us',
    createdByUsers: 'created by users',
    joinOurCommunity: 'Join Our Community',
    title: 'jAIn in numbers',
    totalDevelopmentPlans: 'Development plans',
    totalVisits: 'Total visits',
    trustMessage: 'Join our growing community of professionals',
  },
  whyJain: {
    description: {
      accented: 'data, development, and organizational readiness',
      firstPart: 'We create a space where ',
      secondPart: ' for every change meet in one place.',
    },
    title: 'Why jAIn?',
  },
};
